import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import { useGoogleAuth } from '@questlabs/auth-js';
import SafeIcon from '../../common/SafeIcon';
import EventReminderModal from '../Modals/EventReminderModal';
import ResourceBookingModal from '../Resources/ResourceBookingModal';
import CreateEventModal from './CreateEventModal';
import EventAttendanceCheckIn from '../../features/events/EventAttendanceCheckIn';
import EventRsvpExport from '../../features/events/EventRsvpExport';
import { loadMyRsvps, saveRsvp, clearRsvp } from '../../features/events/eventRsvpApi';
import { useMemorialOwner } from '../../hooks/useMemorialOwner';
import { usePlatformConfig } from '../../hooks/usePlatformConfig';

const {
  FiCalendar, FiMapPin, FiVideo, FiCheckCircle,
  FiBell, FiX, FiExternalLink, FiHeart, FiArrowRight,
  FiPlus, FiRadio, FiGlobe, FiInfo, FiUsers,
} = FiIcons;

// ── Platform badge shown on event cards ───────────────────────────────────────
const PLATFORM_META = {
  youtube:   { emoji: '▶️', label: 'YouTube Live',   color: 'bg-red-50 border-red-200 text-red-700'     },
  facebook:  { emoji: '📘', label: 'Facebook Live',  color: 'bg-blue-50 border-blue-200 text-blue-700'  },
  instagram: { emoji: '📸', label: 'Instagram Live', color: 'bg-pink-50 border-pink-200 text-pink-700'  },
  twitch:    { emoji: '🟣', label: 'Twitch',         color: 'bg-purple-50 border-purple-200 text-purple-700' },
  zoom:      { emoji: '🔵', label: 'Zoom',           color: 'bg-blue-50 border-blue-200 text-blue-700'  },
  gmeet:     { emoji: '🟢', label: 'Google Meet',    color: 'bg-green-50 border-green-200 text-green-700' },
  teams:     { emoji: '🟣', label: 'Teams',          color: 'bg-indigo-50 border-indigo-200 text-indigo-700' },
  jitsi:     { emoji: '🟠', label: 'Jitsi Meet',     color: 'bg-orange-50 border-orange-200 text-orange-700' },
  native:    { emoji: '🔴', label: 'Native Stream',  color: 'bg-red-50 border-red-200 text-red-700'     },
};

const PlatformTag = ({ platformId }) => {
  const meta = PLATFORM_META[platformId];
  if (!meta) return null;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${meta.color}`}>
      {meta.emoji} {meta.label}
    </span>
  );
};

// ── Platform info strip for owners ────────────────────────────────────────────
const PlatformInfoStrip = ({ config, type }) => {
  const cfg = type === 'webinar' ? config.webinar : config.streaming;
  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-2xl border text-[10px] font-bold ${cfg.isOverride ? 'bg-green-50 border-green-200 text-green-700' : 'bg-blue-50 border-blue-200 text-blue-700'}`}>
      <SafeIcon icon={type === 'webinar' ? FiVideo : FiRadio} className="w-3.5 h-3.5 flex-shrink-0" />
      <span>
        {type === 'webinar' ? 'Meeting' : 'Streaming'} platform:{' '}
        <strong>{cfg.emoji} {cfg.label}</strong>
        {' '}—{' '}
        <span className="opacity-70">{cfg.isOverride ? 'Memorial override active' : 'Global default'}</span>
      </span>
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────
const TabEvents = ({ memorial }) => {
  const { isOwner } = useMemorialOwner(memorial);
  const { user } = useGoogleAuth();
  const navigate  = useNavigate();
  const config    = usePlatformConfig(memorial?.id);

  const [events, setEvents]             = useState(memorial?.events || []);
  const [rsvpStatus, setRsvpStatus]     = useState({});
  const [rsvpRecords, setRsvpRecords]   = useState({});
  const [rsvpNotice, setRsvpNotice]     = useState('');
  const [showToast, setShowToast]       = useState(false);
  const [reminderEvent, setReminderEvent] = useState(null);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [currentTime, setCurrentTime]   = useState(new Date());
  const [attendanceOpenId, setAttendanceOpenId] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Load this person's saved RSVPs whenever they sign in or out.
  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setRsvpStatus({});
      setRsvpRecords({});
      return undefined;
    }
    loadMyRsvps().then((res) => {
      if (cancelled) return;
      if (res.status !== 'success') {
        setRsvpNotice('Your saved RSVPs could not be loaded right now.');
        return;
      }
      const statuses = {};
      Object.entries(res.map).forEach(([eventId, rec]) => { statuses[eventId] = rec.status; });
      setRsvpRecords(res.map);
      setRsvpStatus(statuses);
    });
    return () => { cancelled = true; };
  }, [user]);

  const handleRSVP = async (event, status) => {
    const key = String(event.id);
    setRsvpNotice('');

    if (!user) {
      setRsvpNotice('Sign in with Google to save your RSVP.');
      return;
    }

    const existing = rsvpRecords[key];

    if (status === null) {
      const res = await clearRsvp(existing);
      if (res?.status !== 'success') {
        setRsvpNotice(res?.error || 'Could not change your RSVP. Please try again.');
        return;
      }
      setRsvpStatus(prev => { const next = { ...prev }; delete next[key]; return next; });
      setRsvpRecords(prev => { const next = { ...prev }; delete next[key]; return next; });
      return;
    }

    const res = await saveRsvp({ event, memorial, status, existing });
    if (res?.status !== 'success') {
      setRsvpNotice(res?.error || 'Could not save your RSVP. Please try again.');
      return;
    }
    const savedId = existing?.id ?? res?.data?.id ?? null;
    setRsvpStatus(prev => ({ ...prev, [key]: status }));
    setRsvpRecords(prev => ({
      ...prev,
      [key]: {
        id: savedId,
        event_id: key,
        memorial_id: String(memorial?.id ?? ''),
        event_title: event?.title ?? '',
        event_date: event?.date ? String(event.date) : '',
        status,
      },
    }));
    if (status === 'yes') {
      setShowToast(true);
      setTimeout(() => setShowToast(false), 4000);
    }
  };

  const isEventLive = (eventDate) => {
    const start = new Date(eventDate).getTime();
    const end   = start + 2 * 60 * 60 * 1000;
    const now   = currentTime.getTime();
    return now >= start && now <= end;
  };

  const handleEventCreated = (newEvent) => {
    setEvents(prev => [...prev, newEvent]);
  };

  return (
    <div className="space-y-8 relative">

      {/* RSVP toast */}
      <AnimatePresence>
        {showToast && (
          <motion.div
            initial={{ opacity: 0, y: 50, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: 20, x: '-50%' }}
            className="fixed bottom-10 left-1/2 z-[100] bg-accent-navy text-white px-8 py-4 rounded-2xl shadow-2xl border border-white/10 flex items-center gap-4 min-w-[320px]"
          >
            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
              <SafeIcon icon={FiCheckCircle} className="w-6 h-6" />
            </div>
            <div>
              <p className="font-bold text-sm">RSVP Confirmed</p>
              <p className="text-xs text-white/60">We've added this to your notifications.</p>
            </div>
            <button onClick={() => setShowToast(false)} className="ml-auto text-white/40 hover:text-white">
              <SafeIcon icon={FiX} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {rsvpNotice && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-amber-200 bg-amber-50 text-amber-800 text-[11px] font-bold">
          <SafeIcon icon={FiInfo} className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1">{rsvpNotice}</span>
          <button onClick={() => setRsvpNotice('')} className="text-amber-800/60 hover:text-amber-800">
            <SafeIcon icon={FiX} className="w-4 h-4" />
          </button>
        </div>
      )}

      {user && <EventRsvpExport memorialName={memorial?.name ?? memorial?.title} />}

      {/* Owner controls */}
      {isOwner && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-[28px] border border-brand-dark p-5 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-black uppercase tracking-widest text-accent-navy">Event Management</p>
              <p className="text-[10px] text-text-muted mt-0.5">Schedule and manage events for this memorial</p>
            </div>
            <button
              onClick={() => setIsCreateOpen(true)}
              className="flex items-center gap-2 px-6 py-3 bg-accent-navy text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all shadow-lg active:scale-[0.98]"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4" />
              Schedule Event
            </button>
          </div>

          {/* Platform status strips */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <PlatformInfoStrip config={config} type="streaming" />
            <PlatformInfoStrip config={config} type="webinar" />
          </div>

          <div className="flex items-center gap-2 text-[10px] text-text-muted bg-brand-light rounded-xl px-3 py-2">
            <SafeIcon icon={FiInfo} className="w-3 h-3 flex-shrink-0 text-accent-gold" />
            Platform credentials are resolved automatically. Configure overrides in{' '}
            <strong className="text-accent-navy">Customization → Platform Preferences</strong>.
          </div>
        </motion.div>
      )}

      {/* Grief Support Banner */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-brand-light/50 border border-brand-dark rounded-[40px] p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-8 group hover:border-accent-gold transition-all duration-300"
      >
        <div className="flex items-center gap-6">
          <div className="w-16 h-16 bg-white rounded-3xl border border-brand-dark flex items-center justify-center text-accent-gold shadow-sm group-hover:scale-110 transition-transform">
            <SafeIcon icon={FiHeart} className="w-8 h-8" />
          </div>
          <div>
            <h4 className="text-2xl font-serif text-accent-navy mb-1">Grief Support Services</h4>
            <p className="text-text-muted text-sm max-w-sm">Professional counseling and emotional support available for you and your family.</p>
          </div>
        </div>
        <button
          onClick={() => setIsBookingOpen(true)}
          className="w-full md:w-auto px-10 py-5 bg-accent-navy text-white rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] shadow-xl shadow-accent-navy/20 hover:bg-accent-gold transition-all transform hover:-translate-y-1 flex items-center justify-center gap-3"
        >
          Book Grief Counselor Appointment
        </button>
      </motion.div>

      {/* Event list */}
      {events.length > 0 ? (
        events.map((event) => {
          const eventDate    = new Date(event.date);
          const currentRsvp  = rsvpStatus[String(event.id)];
          const isLive       = event.type === 'Livestream' && isEventLive(event.date);
          const platformMeta = event.platform ? PLATFORM_META[event.platform] : null;

          return (
            <div
              key={event.id}
              className={`bg-white rounded-[40px] shadow-sm border ${isLive ? 'border-red-500 ring-2 ring-red-500/20' : 'border-brand-dark'} overflow-hidden group hover:border-accent-gold transition-all duration-300`}
            >
              <div className={`${isLive ? 'bg-red-600' : 'bg-accent-navy'} text-white px-10 py-6 flex justify-between items-center gap-4`}>
                <div className="flex items-center gap-4 flex-wrap">
                  <h3 className="font-serif text-3xl tracking-wide">{event.title}</h3>
                  {isLive && (
                    <motion.span
                      animate={{ opacity: [1, 0.5, 1] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      className="bg-white text-red-600 text-[10px] px-3 py-1 rounded-full font-black uppercase tracking-widest flex items-center gap-2"
                    >
                      <span className="w-2 h-2 bg-red-600 rounded-full animate-pulse" /> Live Now
                    </motion.span>
                  )}
                  {currentRsvp === 'yes' && (
                    <span className="bg-green-500/20 text-green-400 text-[10px] px-3 py-1 rounded-full font-black uppercase tracking-tighter border border-green-500/20">Attending</span>
                  )}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <button
                    onClick={() => navigate(`/memorial/${memorial.id}/event/${event.id}`)}
                    className="bg-white/10 backdrop-blur-sm text-[9px] px-5 py-2 rounded-full uppercase tracking-widest font-black border border-white/10 flex items-center gap-2 hover:bg-white/20 transition-all"
                  >
                    View Details <SafeIcon icon={FiArrowRight} />
                  </button>
                  <span className="bg-white/10 backdrop-blur-sm text-[10px] px-5 py-2 rounded-full uppercase tracking-widest font-black border border-white/10">
                    {event.type}
                  </span>
                </div>
              </div>

              <div className="p-10 md:flex gap-16">
                <div className="md:w-2/3 space-y-8">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
                    {/* Date */}
                    <div className="flex items-start gap-5">
                      <div className="w-12 h-12 rounded-2xl bg-brand-light flex items-center justify-center text-accent-gold flex-shrink-0 border border-brand-dark/50 shadow-sm">
                        <SafeIcon icon={FiCalendar} className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="text-[10px] text-text-muted uppercase tracking-[0.2em] font-black mb-2">Timing</p>
                        <p className="font-bold text-lg text-accent-navy">
                          {eventDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                        </p>
                        <p className="text-sm text-text-muted font-medium">
                          {eventDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}
                        </p>
                      </div>
                    </div>
                    {/* Location */}
                    <div className="flex items-start gap-5">
                      <div className="w-12 h-12 rounded-2xl bg-brand-light flex items-center justify-center text-accent-gold flex-shrink-0 border border-brand-dark/50 shadow-sm">
                        <SafeIcon icon={FiMapPin} className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="text-[10px] text-text-muted uppercase tracking-[0.2em] font-black mb-2">Location</p>
                        <p className="font-bold text-lg text-accent-navy line-clamp-1">{event.location}</p>
                        {platformMeta && <PlatformTag platformId={event.platform} />}
                      </div>
                    </div>
                  </div>

                  <div className="pt-8 border-t border-brand-light">
                    <p className="text-text-main font-light leading-relaxed text-lg line-clamp-3">{event.description}</p>
                    <button
                      onClick={() => navigate(`/memorial/${memorial.id}/event/${event.id}`)}
                      className="mt-4 text-[10px] font-black uppercase tracking-widest text-accent-gold hover:text-accent-navy transition-colors"
                    >
                      Read more about this service
                    </button>
                  </div>

                  {isOwner && event.type !== 'Livestream' && (
                    <div>
                      <button
                        type="button"
                        onClick={() => setAttendanceOpenId(attendanceOpenId === event.id ? null : event.id)}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl border border-brand-dark text-[10px] font-black uppercase tracking-widest text-accent-navy hover:border-accent-gold transition-all"
                      >
                        <SafeIcon icon={FiUsers} className="w-3.5 h-3.5" />
                        {attendanceOpenId === event.id ? 'Hide Attendance' : 'Manage Attendance'}
                      </button>
                      {attendanceOpenId === event.id && (
                        <EventAttendanceCheckIn event={event} memorial={memorial} />
                      )}
                    </div>
                  )}
                </div>

                {/* RSVP / Live panel */}
                <div className={`md:w-1/3 mt-10 md:mt-0 ${isLive ? 'bg-red-50' : 'bg-brand-light/40'} border ${isLive ? 'border-red-100' : 'border-brand-dark'} p-8 rounded-[32px] flex flex-col justify-center text-center`}>
                  {isLive ? (
                    <div className="py-6 flex flex-col items-center">
                      <div className="w-20 h-20 bg-red-100 text-red-600 rounded-full flex items-center justify-center mb-6 shadow-sm border border-red-200">
                        <SafeIcon icon={FiVideo} className="w-10 h-10" />
                      </div>
                      <p className="font-serif text-2xl text-accent-navy mb-2">Service is Live</p>
                      <p className="text-sm text-text-muted mb-4 px-4">The memorial ceremony is currently being broadcasted.</p>
                      {event.platform && <div className="mb-6"><PlatformTag platformId={event.platform} /></div>}
                      <button
                        onClick={() => navigate('/live-events')}
                        className="w-full bg-red-600 text-white py-5 rounded-2xl font-bold text-lg shadow-xl shadow-red-600/20 hover:bg-red-700 transition-all transform hover:-translate-y-1 flex items-center justify-center gap-3"
                      >
                        Join Live Stream <SafeIcon icon={FiExternalLink} className="w-5 h-5" />
                      </button>
                    </div>
                  ) : currentRsvp ? (
                    <div className="py-6">
                      <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <SafeIcon icon={FiCheckCircle} className="w-8 h-8" />
                      </div>
                      <p className="font-serif text-xl text-accent-navy mb-2">Confirmed</p>
                      <button onClick={() => handleRSVP(event, null)} className="text-[10px] font-black uppercase tracking-widest text-text-muted hover:text-accent-gold underline">Change RSVP</button>
                    </div>
                  ) : (
                    <>
                      <p className="text-[11px] font-black uppercase tracking-widest text-accent-navy mb-8">Will you be attending?</p>
                      <div className="space-y-4">
                        <button onClick={() => handleRSVP(event, 'yes')} className="w-full btn-primary py-4 rounded-2xl shadow-lg hover:shadow-xl transition-all">
                          Yes, I'll attend
                        </button>
                        <button onClick={() => handleRSVP(event, 'no')} className="w-full py-4 text-xs font-black uppercase tracking-widest text-text-muted hover:text-accent-navy transition-colors bg-white border-2 border-brand-dark rounded-2xl">
                          I can't make it
                        </button>
                      </div>
                    </>
                  )}

                  {!isLive && (
                    <div className="mt-8 pt-8 border-t border-brand-dark/50 flex flex-col gap-4">
                      <div className="flex items-center justify-center gap-2 text-[9px] text-accent-gold font-black uppercase tracking-[0.2em]">
                        <SafeIcon icon={FiVideo} className="w-3.5 h-3.5" />
                        {event.platform ? `${PLATFORM_META[event.platform]?.label || 'Live'} Available` : 'LIVESTREAM AVAILABLE'}
                      </div>
                      <button
                        onClick={() => setReminderEvent(event)}
                        className="flex items-center justify-center gap-3 py-4 bg-white hover:bg-brand-light border-2 border-brand-dark rounded-2xl text-[10px] font-black uppercase tracking-widest text-accent-navy transition-all group"
                      >
                        <SafeIcon icon={FiBell} className="w-3.5 h-3.5 text-accent-gold group-hover:scale-110 transition-transform" />
                        Remind Me
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className="text-center py-32 bg-white rounded-[50px] border-2 border-dashed border-brand-dark">
          <div className="w-24 h-24 bg-brand-light rounded-3xl flex items-center justify-center text-text-muted/20 mx-auto mb-8 border border-brand-dark/50">
            <SafeIcon icon={FiCalendar} className="w-12 h-12" />
          </div>
          <h4 className="text-3xl font-serif text-accent-navy mb-4">No Services Scheduled</h4>
          <p className="text-text-muted max-w-sm mx-auto font-light leading-relaxed mb-8">
            {isOwner ? 'Use the "Schedule Event" button above to add the first event.' : 'The family hasn\'t listed any public services at this time. Please check back later.'}
          </p>
          {isOwner && (
            <button onClick={() => setIsCreateOpen(true)}
              className="inline-flex items-center gap-2 px-8 py-4 bg-accent-navy text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all shadow-lg">
              <SafeIcon icon={FiPlus} className="w-4 h-4" /> Schedule First Event
            </button>
          )}
        </div>
      )}

      {/* Modals */}
      {reminderEvent && (
        <EventReminderModal
          isOpen={!!reminderEvent}
          onClose={() => setReminderEvent(null)}
          eventTitle={reminderEvent.title}
          eventDate={reminderEvent.date}
          eventLocation={reminderEvent.location}
          eventDescription={reminderEvent.description}
        />
      )}

      <ResourceBookingModal
        isOpen={isBookingOpen}
        onClose={() => setIsBookingOpen(false)}
        resourceType="Grief Counselor"
      />

      <CreateEventModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        memorial={memorial}
        onEventCreated={handleEventCreated}
      />
    </div>
  );
};

export default TabEvents;